// global Config

import {mapValues} from 'lodash';

const configBase = {
    baseUrl: Config.base.twisterUrl,
    contentType: 'application/json',
    crossDomain: false,
    method: 'GET',
    queryType: 'rsql',
    responseType: 'json',
    timeout: 60000,
};

const servicesConfig = {
    auditDetail: {
        routerKeys: [{routerKey: 'auditId', searchType: 'rsqlQuery'}],
        path: 'audits',
        consumerServiceDataKeys: ['mediaItemDetailAudit', 'auditsRelated'],
    },
    audits: {
        activeRowKeys: ['auditId'],
        formName: 'audits',
        path: 'audits',
        route: 'audits',
    },
    auditsRelated: {
        providerSearchKeys: [{recordKey: 'correlationId', queryKey: 'correlationId', searchType: 'rsqlQuery'}],
        providerServiceDataKey: 'auditDetail',
        path: 'audits',
    },
    authenticate: {
        getParams: {grant_type: 'client_credentials'},
        path: 'oauth/token',
        method: 'POST',
        timeout: 10000,
    },
    bestMatches: {
        formName: 'bestMatches',
        path: 'bestmatches',
        routerKeys: [{routerKey: 'guid', searchType: 'path'}],
        timeout: 60000,
    },
    catalogItemsMissingEid: {
        getParams: {limit: 1000},
        baseUrl: Config.base.externalJsonUrl,
        path: 'catalogitemsmissingeid',
    },
    createExternalNetwork: {
        path: 'networks/externalnetworks/mappings',
        method: 'POST',
        timeout: 300000,
    },
    deleteOffering: {
        path: 'offerings',
        method: 'DELETE',
    },
    deleteRule: {
        path: 'rules',
        method: 'DELETE',
    },
    deleteShell: {
        path: 'amsprograms',
        method: 'DELETE',
    },
    displayPaths: {
        activeRowKeys: ['networkDisplayPathId'],
        formName: 'displayPaths',
        path: 'displaypaths',
        providerFormFields: [{fieldName: 'network.name', queryKey: 'networkName'}],
        providerFormName: 'selectNetworkForDisplayPaths',
        route: 'display-paths',
    },
    displayPathDetail: {
        path: 'displaypaths',
        routerKeys: [{routerKey: 'networkDisplayPathId', searchType: 'path'}],
    },
    displayPathModifiers: {
        activeRowKeys: ['networkDisplayPathModifierId'],
        formName: 'displayPathModifiers',
        path: 'displaypathmodifiers',
        providerFormFields: [{fieldName: 'network.name', queryKey: 'networkName'}],
        providerFormName: 'selectNetworkForDisplayPathModifiers',
        route: 'display-path-modifiers',
    },
    displayPathModifierDetail: {
        path: 'displaypathmodifiers',
        routerKeys: [{routerKey: 'networkDisplayPathModifierId', searchType: 'path'}],
    },
    failures: {
        providerSearchKeys: [{recordKey: 'correlationId', searchType: 'path'}],
        providerServiceDataKey: 'mediaItemDetail',
        path: 'violations',
    },
    fvodOrdering: {
        path: 'mediaitems/fvodOrdering',
        method: 'GET',
    },
    fvodOrderingSave: {
        path: 'mediaitems/fvodOrdering',
        method: 'POST',
    },
    flaggedMetadataDetail: {
        path: 'flaggedmetadata',
        routerKeys: [{routerKey: 'flaggedMetadataId', searchType: 'path'}],
    },
    flaggedMetadata: {
        activeRowKeys: ['flaggedMetadataId'],
        formName: 'flaggedMetadata',
        path: 'flaggedmetadata',
        route: 'investigation/flagged-metadata',
        exclude_from_rsql: ['amsPrograms.title', 'amsPrograms.echostarId', 'amsPrograms.echostarEventString', 'genres'],
        paramKey: {
            'amsPrograms.title': 'amsProgramsTitle',
            'amsPrograms.echostarId': 'echostarId',
            'amsPrograms.echostarEventString': 'echostarEventString',
            genres: 'genreNames',
        },
    },
    genres: {
        path: 'genres',
    },
    bibleMaster: {
        path: 'amsprograms/genre_bible_master',
        searchOnly: true,
        method: 'GET',
    },
    genresSelect: {
        path: 'genres',
        searchOnly: true,
    },
    graceNoteUpdate: {
        path: 'amsprograms/shells',
        method: 'POST',
    },
    graceNoteGuidToEid: {
        path: 'mediaitems',
        method: 'GET',
    },
    health: {
        path: 'health',
    },
    mapExternalNetwork: {
        path: 'networks/externalnetworks/mappings',
        method: 'PUT',
        timeout: 300000,
    },
    massActionDetail: {
        path: 'mediaitems/states',
        routerKeys: [{routerKey: 'correlationId', queryKey: 'correlationId', searchType: 'rsqlQuery'}],
    },
    massActionResults: {
        activeRowKeys: ['correlationId'],
        formName: 'massActionResults',
        path: 'mediaitems/states',
        route: 'investigation/mass-action-results',
    },
    mediaItems: {
        activeRowKeys: ['guid'],
        defaultSort: ['mediaItemId', 'desc'],
        exclude_from_rsql: ['service.const.serviceName', 'amsFranchise.const.echostarContentType'],
        formName: 'mediaItems',
        getParams: {adult: Config.base.matureContent},
        path: 'mediaitems',
        route: 'media-items',
        searchServiceDataKeys: ['networksSelect', 'providersSelect', 'servicesSelect'],
    },
    mediaItemAsyncReport: {
        activeRowKeys: ['guid'],
        formName: 'mediaItemAsyncReport',
        method: 'POST',
        path: 'mediaitems/async',
        getParams: {adult: Config.base.matureContent, copyToS3: Config.base.uploadMediaItemsReportsToS3},
        payloadType: 'params',
        providerServiceDataKey: 'mediaItemDetail',
        queryType: 'rsql',
    },
    mediaItemDuplicateTitlesAsyncReport: {
        activeRowKeys: ['guid'],
        formName: 'mediaItemDuplicateTitlesAsyncReport',
        method: 'POST',
        path: 'mediaitems/async/duplicateTitles',
        getParams: {copyToS3: Config.base.uploadMediaItemsReportsToS3},
        payloadType: 'params',
        providerServiceDataKey: 'mediaItemDetail',
        queryType: 'queryParams',
        exclude_from_rsql: ['amsFranchise.echostarContentType', 'service.serviceName'],
        paramKey: {
            'amsFranchise.echostarContentType': 'contentType',
            'service.serviceName': 'service',
        },
    },
    mediaItemsEnhanced: {
        activeRowKeys: ['guid'],
        defaultSort: ['mediaItemId', 'desc'],
        formName: 'mediaItemsEnhanced',
        getParams: {adult: Config.base.matureContent},
        path: 'mediaitems',
        route: 'investigation/enhanced-view',
        searchServiceDataKeys: ['networksSelect', 'providersSelect', 'servicesSelect'],
    },
    mediaItemsMapped: {
        formName: 'mediaItemsMapped',
        defaultSort: ['mediaItemId', 'desc'],
        path: 'mediaitems',
        routerKeys: [
            {
                routerKey: 'echostarId',
                searchType: 'rsqlQuery',
                queryKey: 'amsEpisode.echostarId||amsFranchise.echostarId',
                defaultValue: '-1',
            },
        ],
    },
    mediaItemCix: {
        path: 'mediaitems/cix',
        routerKeys: [{routerKey: 'guid', searchType: 'path'}],
    },
    mediaItemDetail: {
        getParams: {adult: Config.base.matureContent},
        path: 'mediaitems',
        routerKeys: [{routerKey: 'guid', searchType: 'rsqlQuery'}],
        consumerServiceDataKeys: [
            'moviesAndProgramsRelated',
            'failures',
            'mediaItemFreewheelAdRequest',
            'mediaItemJsonReceived',
            'mediaItemJsonRadish',
            'mediaItemJsonCatalog',
        ],
    },
    mediaItemDetailAudit: {
        providerServiceDataKey: 'auditDetail',
        providerSearchKeys: [{recordKey: 'correlationId', queryKey: 'correlationId', searchType: 'rsqlQuery'}],
        getParams: {adult: Config.base.matureContent},
        path: 'mediaitems',
    },
    mediaItemDetailViolation: {
        providerServiceDataKey: 'violationDetail',
        providerSearchKeys: [{recordKey: 'correlationId', queryKey: 'correlationId', searchType: 'rsqlQuery'}],
        getParams: {adult: Config.base.matureContent},
        path: 'mediaitems',
    },
    mediaItemIvi: {
        path: 'mediaitems/ivi',
        routerKeys: [{routerKey: 'guid', searchType: 'path'}],
    },
    mediaItemJsonReceived: {
        providerSearchKeys: [{recordKey: 'externalGuid', searchType: 'path'}],
        providerServiceDataKey: 'mediaItemDetail',
        path: 'mediaitems/json/received',
    },
    mediaItemJsonRadish: {
        formName: 'mediaItemJsonRadish',
        providerSearchKeys: [
            {recordKey: 'amsFranchise.echostarContentType', queryKey: 'echostarContentType', searchType: 'rsqlQuery'},
            {recordKey: 'amsFranchise.echostarId', queryKey: 'echostarId', searchType: 'rsqlQuery'},
            {recordKey: 'amsEpisode.echostarId', queryKey: 'echostarEpisodeId', searchType: 'rsqlQuery'},
        ],
        queryType: 'queryParams',
        providerServiceDataKey: 'mediaItemDetail',
        baseUrl: Config.base.externalJsonUrl,
        path: 'radish',
    },
    mediaItemFreewheelAdRequest: {
        formName: 'mediaItemFreewheelAdRequest',
        providerSearchKeys: [{recordKey: 'externalGuid', searchType: 'path'}],
        providerServiceDataKey: 'mediaItemDetail',
        baseUrl: Config.base.microburst,
        path: 'v1/vod/freewheelurl',
    },
    mediaItemJsonCatalog: {
        formName: 'mediaItemJsonCatalog',
        providerSearchKeys: [
            {recordKey: 'amsFranchise.echostarId', queryKey: 'echostarId', searchType: 'rsqlQuery'},
            {recordKey: 'amsEpisode.echostarId', queryKey: 'echostarEpisodeId', searchType: 'rsqlQuery'},
            {recordKey: 'offerings.0.resourceId', queryKey: 'resourceId', searchType: 'rsqlQuery'},
            {recordKey: 'externalGuid', queryKey: 'externalGuid', searchType: 'rsqlQuery'},
        ],
        queryType: 'queryParams',
        providerServiceDataKey: 'mediaItemDetail',
        baseUrl: Config.base.externalJsonUrl,
        path: 'catalog',
    },
    markerFingerprint: {
        path: 'marker/fingerprint',
        method: 'GET',
    },
    moviesAndPrograms: {
        activeRowKeys: ['echostarId', 'tmsId'],
        defaultSort: [
            ['echostarId', 'desc'],
            ['tmsId', 'desc'],
        ],
        route: 'movies-and-programs',
        formName: 'moviesAndPrograms',
        path: 'amsprograms',
    },
    moviesAndProgramsDetail: {
        path: 'amsprograms',
        routerKeys: [
            {routerKey: 'echostarId', searchType: 'rsqlQuery'},
            {routerKey: 'roviProgramId', searchType: 'rsqlQuery'},
            {routerKey: 'tmsId', searchType: 'rsqlQuery'},
        ],
    },
    moviesAndProgramsEpisodes: {
        formName: 'moviesAndProgramsEpisodes',
        defaultSort: [
            ['echostarId', 'desc'],
            ['roviProgramId', 'desc'],
        ],
        path: 'amsprograms',
        routerKeys: [
            {routerKey: 'roviProgramId', searchType: 'rsqlQuery', queryKey: 'seriesId'},
            {routerKey: 'echostarId', searchType: 'rsqlQuery', queryKey: 'seriesId'},
        ],
        rsqlQuery: {echostarContentType: 'SRE'},
    },
    moviesAndProgramsForNetworkDisplayPath: {
        defaultSort: [
            ['echostarId', 'desc'],
            ['roviProgramId', 'desc'],
        ],
        formName: 'moviesAndProgramsForNetworkDisplayPath',
        path: 'amsprograms',
        rsqlQuery: {echostarContentType: '!SRE'},
    },
    moviesAndProgramsMappingEpisodes: {
        defaultSort: [
            ['echostarId', 'desc'],
            ['roviProgramId', 'desc'],
        ],
        noFetchOnRegister: true,
        formName: 'moviesAndProgramsMappingEpisodes',
        path: 'amsprograms',
    },
    moviesAndProgramsMappingFranchise: {
        noFetchOnRegister: true,
        formName: 'moviesAndProgramsMappingFranchise',
        path: 'amsprograms',
    },
    moviesAndProgramsRelated: {
        providerSearchKeys: [
            {
                recordKey: 'amsEpisode.echostarId||amsFranchise.echostarId',
                searchType: 'rsqlQuery',
                queryKey: 'echostarId',
                defaultValue: '-1',
            },
        ],
        providerServiceDataKey: 'mediaItemDetail',
        path: 'amsprograms',
    },
    networks: {
        activeRowKeys: ['networkId'],
        formName: 'networks',
        path: 'networks',
        route: 'networks',
    },
    networksMapping: {
        path: 'networks',
    },
    networkDetail: {
        path: 'networks',
        routerKeys: [{routerKey: 'networkId', searchType: 'rsqlQuery'}],
    },
    networksSelect: {
        path: 'networks',
        searchOnly: true,
    },
    propertiesGet: {
        baseUrl: '/',
        path: '/json/twister_properties_get',
        method: 'GET',
    },
    propertiesPut: {
        baseUrl: '/',
        path: '/json/twister_properties_put',
        method: 'PUT',
    },
    propertiesPost: {
        baseUrl: '/',
        path: '/json/twister_properties_post',
        method: 'POST',
    },
    providers: {
        activeRowKeys: ['partnerId'],
        path: 'providers',
        formName: 'providers',
        route: 'providers',
    },
    providersSelect: {
        path: 'providers',
        searchOnly: true,
    },
    providerDetail: {
        path: 'providers',
        routerKeys: [{routerKey: 'partnerId', searchType: 'rsqlQuery'}],
    },
    refreshToken: {
        getParams: {grant_type: 'refresh_token'},
        path: 'oauth/token',
        method: 'POST',
        timeout: 10000,
    },
    resetPassword: {
        activeRowKeys: ['token'],
        route: 'reset-password',
    },
    roles: {
        activeRowKeys: ['roleId'],
        formName: 'roles',
        path: 'roles',
        route: 'roles',
    },
    roleDetail: {
        path: 'roles',
        routerKeys: [{routerKey: 'roleId', searchType: 'path'}],
    },
    rolesTree: {
        path: 'roles',
    },
    rules: {
        activeRowKeys: ['ruleId'],
        route: 'rules',
        formName: 'rules',
        path: 'rules',
    },
    rulesDetail: {
        path: 'rules',
        routerKeys: [{routerKey: 'ruleId', searchType: 'rsqlQuery'}],
    },
    scopesTree: {
        path: 'roles/legacy',
    },
    servicesSelect: {
        path: 'services',
        searchOnly: true,
    },
    stbActionsGet: {
        baseUrl: '/',
        path: '/json/stb_actions_get',
        method: 'GET',
    },
    stbActionsDelete: {
        baseUrl: '/',
        path: '/json/stb_actions_delete',
        method: 'DELETE',
    },
    stbActionsPost: {
        baseUrl: '/',
        path: '/json/stb_actions_post',
        method: 'POST',
    },
    stbActionsPut: {
        baseUrl: '/',
        path: '/json/stb_actions_put',
        method: 'PUT',
    },
    unmappedExternalNetworks: {
        getParams: {unmapped: 'true'},
        path: 'networks/externalnetworks',
    },
    unmappedMediaItems: {
        formName: 'unmappedMediaItems',
        activeRowKeys: ['guid'],
        defaultSort: ['mediaItemId', 'desc'],
        route: 'media-items',
        getParams: {
            activeOnly: true,
            adult: Config.base.matureContent,
            fullyMapped: false,
        },
        path: 'mediaitems',
        rsqlQuery: {fullyMapped: false, activeOnly: true},
        searchServiceDataKeys: ['networksSelect', 'providersSelect', 'servicesSelect'],
    },
    userDetail: {
        path: 'users',
        routerKeys: [{routerKey: 'clientId', searchType: 'rsqlQuery'}],
    },
    users: {
        activeRowKeys: ['clientId'],
        route: 'admin/users',
        formName: 'users',
        path: 'users',
    },
    vfb: {
        activeRowKeys: ['networkId'],
        formName: 'vfb',
        path: 'vfb',
        route: 'vfb',
    },
    violationDetail: {
        routerKeys: [{routerKey: 'violationId', searchType: 'rsqlQuery'}],
        path: 'violations',
        consumerServiceDataKeys: ['mediaItemDetailViolation', 'violationsRelated'],
    },
    violations: {
        activeRowKeys: ['violationId'],
        formName: 'violations',
        path: 'violations',
        route: 'investigation/violations',
    },
    violationsRelated: {
        providerSearchKeys: [{recordKey: 'correlationId', queryKey: 'correlationId', searchType: 'rsqlQuery'}],
        providerServiceDataKey: 'violationDetail',
        path: 'violations',
    },
    standardizedGenres: {
        path: 'amsprograms/standardize',
        method: 'POST',
    },
};

export default mapValues(servicesConfig, (config) => ({...configBase, ...config}));
